const DAY_MS = 24 * 60 * 60 * 1000;

/** Any per-day API row: the affiliate endpoints all key their days this way. */
interface DailyRow {
  day_start_unix_ms: number;
}

/**
 * Turns a newest-first daily series into chart columns, oldest to newest.
 *
 * Shared by every 14-day affiliate chart because three behaviours have to agree
 * across them, and each one exists to stop the chart overstating activity:
 *
 * - Order is flipped. The endpoints return newest-first; charts read left to right.
 * - Interior gaps become zero columns. Days with no activity are omitted from the
 *   responses, so plotting only the returned rows would collapse a multi-day lull
 *   into a single step.
 * - The window is padded back to `windowDays`. One active day would otherwise fill
 *   the whole card and read as a busy fortnight.
 *
 * The window anchors on the newest *returned* day, never on today. After an inactive
 * stretch that day is older than today, and relabelling it as today would misdate
 * every column.
 *
 * `toBar` receives the day's timestamp and its row when one exists, so each caller
 * decides what a zero day looks like for its own metric.
 */
export function fillDailyWindow<T extends DailyRow, B>(
  days: readonly T[],
  windowDays: number,
  toBar: (dayStartUnixMs: number, present: T | undefined) => B,
): B[] {
  if (days.length === 0) return [];

  const byDay = new Map(days.map((day) => [day.day_start_unix_ms, day]));
  const stamps = days.map((day) => day.day_start_unix_ms);
  const newest = Math.max(...stamps);
  const oldestReturned = Math.min(...stamps);
  // Whichever reaches further back: the padded window, or the data itself.
  const oldest = Math.min(oldestReturned, newest - (Math.max(1, windowDays) - 1) * DAY_MS);
  const columns = Math.round((newest - oldest) / DAY_MS) + 1;

  return Array.from({ length: columns }, (_, offset) => {
    const dayStartUnixMs = oldest + offset * DAY_MS;
    return toBar(dayStartUnixMs, byDay.get(dayStartUnixMs));
  });
}
